import React, { useState, useEffect, useCallback, useRef } from 'react';
import { smsAPI, customerAPI, API_BASE_URL } from '../../services/api';
import { formatPhone } from '../../utils/phone';
import CameraCapture from '../CameraCapture/CameraCapture';
import './smsInbox.css';

// Two-way SMS inbox (Podium/Engage-style). Left pane lists conversations; right
// pane shows the selected thread with a reply composer. Conversations and the
// open thread poll so inbound replies appear without a manual refresh.
const SmsInbox = () => {
  const [conversations, setConversations] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  // 'inbox' = conversations grouped by contact. 'automated' = a flat, newest-first
  // log of everything the system texted on its own, so "did that automation
  // actually go out?" is one click rather than a hunt through threads.
  const [view, setView] = useState('inbox');
  const [automated, setAutomated] = useState([]);
  const [loadingAutomated, setLoadingAutomated] = useState(false);
  // Selected conversation: { customerId, name }. May be a brand-new draft (a
  // customer with no messages yet) started via "New message".
  const [selected, setSelected] = useState(null);
  const [thread, setThread] = useState([]);
  const [loadingThread, setLoadingThread] = useState(false);

  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  // Pending MMS image attachments: [{ key, contentType, previewUrl }].
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const fileInputRef = useRef(null);
  // Lightbox: full-size media URL when a thumbnail is clicked.
  const [lightbox, setLightbox] = useState(null);

  // New-message customer search.
  const [composing, setComposing] = useState(false);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);


  const loadConversations = useCallback(async () => {
    try {
      const res = await smsAPI.getConversations();
      setConversations(res.data || []);
    } catch (err) {
      console.error('Failed to load conversations:', err);
    } finally {
      setLoadingList(false);
    }
  }, []);

  const loadThread = useCallback(async (phone, { silent } = {}) => {
    if (!phone) return;
    if (!silent) setLoadingThread(true);
    try {
      const res = await smsAPI.getConversation(phone);
      setThread(res.data?.messages || []);
    } catch (err) {
      console.error('Failed to load thread:', err);
    } finally {
      if (!silent) setLoadingThread(false);
    }
  }, []);

  const loadAutomated = useCallback(async () => {
    try {
      const res = await smsAPI.getAutomated(150);
      setAutomated(res.data || []);
    } catch (err) {
      console.error('Failed to load automated messages:', err);
    } finally {
      setLoadingAutomated(false);
    }
  }, []);

  // Initial load + poll the conversation list every 20s.
  useEffect(() => {
    loadConversations();
    const id = setInterval(loadConversations, 20000);
    return () => clearInterval(id);
  }, [loadConversations]);

  // Same polling for the automated log, but only while it's the visible tab —
  // it's a verification view, not something to keep fetching in the background.
  useEffect(() => {
    if (view !== 'automated') return;
    setLoadingAutomated(true);
    loadAutomated();
    const id = setInterval(loadAutomated, 20000);
    return () => clearInterval(id);
  }, [view, loadAutomated]);

  // When a conversation is selected, load its thread and poll it every 12s.
  useEffect(() => {
    if (!selected?.phone) {
      setThread([]);
      return;
    }
    loadThread(selected.phone);
    const id = setInterval(() => loadThread(selected.phone, { silent: true }), 12000);
    return () => clearInterval(id);
  }, [selected, loadThread]);

  // Open a conversation at the newest message, and stay there as new ones land —
  // without dragging anyone out of older messages they are deliberately reading.
  //
  // A long thread used to open somewhere in the middle. Two causes, both of which
  // needed fixing:
  //
  //   * It animated. A smooth scroll from the top of a long thread takes time, and
  //     any click or wheel during it cancels the animation wherever it had reached.
  //   * MMS photos have no dimensions until they load, so the thread grows taller
  //     after the first layout. Whatever the bottom was when the scroll started is
  //     no longer the bottom by the time it arrives.
  //
  // So: jump instantly when the conversation opens, and jump again as images
  // resolve. Only a new message in a thread already open eases into view.
  const threadBoxRef = useRef(null);
  const openedPhoneRef = useRef(null);

  const scrollToBottom = useCallback((smooth) => {
    const box = threadBoxRef.current;
    if (!box) return;
    // scrollTop rather than scrollIntoView: this scrolls the thread and nothing
    // else, where scrollIntoView can also shift the page around it on mobile.
    box.scrollTo({ top: box.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
  }, []);

  useEffect(() => {
    const box = threadBoxRef.current;
    if (!box) return;

    const isNewConversation = openedPhoneRef.current !== selected?.phone;
    openedPhoneRef.current = selected?.phone ?? null;

    if (isNewConversation) {
      scrollToBottom(false);

      // Images finish after this render and make the thread taller. Re-pin the
      // bottom as each one resolves, then stop listening — without this the view
      // sits wherever the pre-image height happened to put it.
      const images = Array.from(box.querySelectorAll('img'));
      const pending = images.filter((img) => !img.complete);
      if (pending.length === 0) return;

      const onSettle = () => scrollToBottom(false);
      pending.forEach((img) => {
        img.addEventListener('load', onSettle);
        img.addEventListener('error', onSettle);
      });
      return () => {
        pending.forEach((img) => {
          img.removeEventListener('load', onSettle);
          img.removeEventListener('error', onSettle);
        });
      };
    }

    // Same conversation, new content — usually the 12-second poll. Follow along
    // only if they were already at the bottom, or reading history would be yanked
    // away every time a message arrived.
    const distanceFromBottom = box.scrollHeight - box.scrollTop - box.clientHeight;
    if (distanceFromBottom < 120) scrollToBottom(true);
  }, [thread, selected?.phone, scrollToBottom]);

  const openConversation = (c) => {
    setError(null);
    setComposing(false);
    setSelected({ phone: c.phone, name: c.displayName, customerId: c.customerId });
    // Clear the unread badge locally; the server marks read on thread fetch.
    setConversations((prev) =>
      prev.map((p) => (p.phone === c.phone ? { ...p, unread: 0 } : p))
    );
  };

  // Mobile: return from the thread/compose pane to the conversation list.
  const backToList = () => {
    setSelected(null);
    setComposing(false);
    setError(null);
  };

  // Upload one image to S3 and add it to the pending attachment tray. Shared by the
  // file picker and the in-app camera.
  const uploadAttachment = async (file) => {
    const res = await smsAPI.uploadMedia(file);
    setAttachments((prev) => [
      ...prev,
      { key: res.data.key, contentType: res.data.contentType, previewUrl: URL.createObjectURL(file) },
    ]);
  };

  const handlePickFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ''; // allow re-selecting the same file
    if (files.length === 0) return;
    setError(null);
    setUploading(true);
    try {
      for (const file of files) {
        if (!file.type.startsWith('image/')) {
          setError('Only image files can be attached.');
          continue;
        }
        await uploadAttachment(file);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to attach image.');
    } finally {
      setUploading(false);
    }
  };

  const handleCameraCapture = async (file) => {
    setShowCamera(false);
    setError(null);
    setUploading(true);
    try {
      await uploadAttachment(file);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to attach photo.');
    } finally {
      setUploading(false);
    }
  };

  const removeAttachment = (key) => {
    setAttachments((prev) => prev.filter((a) => a.key !== key));
  };

  const handleSend = async () => {
    const body = draft.trim();
    if ((!body && attachments.length === 0) || !selected?.phone || sending) return;
    setSending(true);
    setError(null);
    try {
      await smsAPI.send({
        toNumber: selected.phone,
        customerId: selected.customerId,
        body,
        mediaKeys: attachments.map((a) => a.key),
      });
      setDraft('');
      setAttachments([]);
      await loadThread(selected.phone, { silent: true });
      loadConversations();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send text.');
    } finally {
      setSending(false);
    }
  };

  const handleComposerKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // New-message customer search (debounced-ish on each keystroke).
  const runSearch = async (term) => {
    setSearch(term);
    if (term.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    try {
      // Contacts and crews/staff together. Only contacts were searchable before, so
      // the only way to reach a crew was to wait for them to text first.
      const [custRes, dirRes] = await Promise.allSettled([
        customerAPI.getAll({ search: term, pageSize: 8 }),
        smsAPI.getDirectory(term),
      ]);

      const contacts = custRes.status === 'fulfilled'
        ? (Array.isArray(custRes.value.data) ? custRes.value.data : custRes.value.data?.items || [])
        : [];

      // Normalised to the same shape the list renders, so crews need no special
      // case beyond the badge. No id, because they are not Customer rows — keying
      // on the phone, which is what a conversation is identified by anyway.
      const crews = dirRes.status === 'fulfilled'
        ? (dirRes.value.data || []).map(d => ({
            id: `crew-${d.phone}`,
            displayName: d.name,
            phone: d.phone,
            isCrew: true,
          }))
        : [];

      setSearchResults([...contacts, ...crews]);
    } catch (err) {
      console.error('Recipient search failed:', err);
    }
  };

  const startConversationWith = (cust) => {
    // A crew entry already carries its number and name, and has no CustomerId —
    // there is no contact record behind it to navigate to.
    if (cust.isCrew) {
      setSelected({ phone: cust.phone, name: cust.displayName, customerId: null });
      setComposing(false);
      setSearch('');
      setSearchResults([]);
      setError(null);
      return;
    }

    const phone = custPhone(cust);
    if (!phone) {
      setError('That contact has no phone number on file.');
      return;
    }
    const name = `${cust.firstName || ''} ${cust.lastName || ''}`.trim() || cust.companyName || 'Unknown';
    setSelected({ phone, name, customerId: cust.id });
    setComposing(false);
    setSearch('');
    setSearchResults([]);
    setError(null);
  };

  const formatTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    return sameDay
      ? d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
      : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const custPhone = (c) => c.mobilePhone || c.mainPhone || c.phone || c.workPhone || '';

  // The backend returns MMS media proxy URLs host-relative (e.g. "/api/sms/media?…")
  // so they resolve against the API origin the browser already uses rather than the
  // ngrok tunnel. Prepend the API base for those; leave any absolute URL untouched.
  const mediaSrc = (url) => (!url || /^https?:\/\//i.test(url) ? url : `${API_BASE_URL}${url}`);

  // Same URL with download=true, which makes the API send Content-Disposition:
  // attachment. Needed because the app and the API are different origins, so the
  // <a download> attribute is ignored and the browser would just open the image.
  const downloadSrc = (url) => {
    const full = mediaSrc(url);
    if (!full) return full;
    return full + (full.includes('?') ? '&' : '?') + 'download=true';
  };

  // Several files can't be triggered in one tick — browsers drop all but the first.
  // Spacing them out is what makes "save all" actually save all of them.
  const downloadAll = (media) => {
    (media || []).forEach((md, i) => {
      setTimeout(() => {
        const a = document.createElement('a');
        a.href = downloadSrc(md.url);
        document.body.appendChild(a);
        a.click();
        a.remove();
      }, i * 400);
    });
  };

  return (
    <div className={`sms-inbox${composing || selected ? ' show-thread' : ''}`}>
      {/* ---------- Conversation list ---------- */}
      <aside className="sms-list">
        <div className="sms-list-header">
          <h2>Messages</h2>
          <button className="sms-new-btn" onClick={() => { setComposing(true); setSelected(null); }}>
            <i className="fas fa-pen"></i> New
          </button>
        </div>

        <div className="sms-view-tabs">
          <button
            className={`sms-view-tab${view === 'inbox' ? ' active' : ''}`}
            onClick={() => setView('inbox')}
          >
            Inbox
          </button>
          <button
            className={`sms-view-tab${view === 'automated' ? ' active' : ''}`}
            onClick={() => setView('automated')}
            title="Every text the system sent on its own — automations and auto-replies"
          >
            <i className="fas fa-bolt"></i> Automated
          </button>
        </div>

        {view === 'automated' ? (
          loadingAutomated ? (
            <div className="sms-list-empty">Loading…</div>
          ) : automated.length === 0 ? (
            <div className="sms-list-empty">
              No automated texts sent yet.<br />
              They'll appear here as automations fire.
            </div>
          ) : (
            <ul className="sms-auto-log">
              {automated.map((m) => (
                <li
                  key={m.id}
                  className={`sms-auto-row${m.status === 'failed' ? ' failed' : ''}`}
                  onClick={() => openConversation({
                    phone: m.phone,
                    displayName: m.displayName,
                    customerId: m.customerId,
                  })}
                >
                  <div className="sms-auto-top">
                    <span className="sms-auto-to">{m.displayName}</span>
                    <span className="sms-conversation-time">{formatTime(m.createdAt)}</span>
                  </div>
                  {(m.poNumber || m.projectName) && (
                    <div className="sms-auto-job">
                      <i className="fas fa-briefcase"></i>{' '}
                      {m.poNumber ? `${m.poNumber} · ` : ''}{m.projectName}
                    </div>
                  )}
                  <div className="sms-auto-body">{m.body}</div>
                  <div className={`sms-auto-status status-${m.status}`}>
                    <i className={`fas ${m.status === 'failed' ? 'fa-triangle-exclamation' : 'fa-paper-plane'}`}></i>
                    {' '}{m.status}
                    {m.errorMessage && <span className="sms-auto-error"> — {m.errorMessage}</span>}
                  </div>
                </li>
              ))}
            </ul>
          )
        ) : loadingList ? (
          <div className="sms-list-empty">Loading…</div>
        ) : conversations.length === 0 ? (
          <div className="sms-list-empty">
            No conversations yet.<br />Start one with <strong>New</strong>.
          </div>
        ) : (
          <ul className="sms-conversations">
            {conversations.map((c) => (
              <li
                key={c.phone}
                className={`sms-conversation${selected?.phone === c.phone ? ' active' : ''}${c.unread > 0 ? ' unread' : ''}`}
                onClick={() => openConversation(c)}
              >
                <div className="sms-conversation-top">
                  <span className="sms-conversation-name">
                    {c.displayName}
                    {!c.isKnown && <span className="sms-unknown-tag">Unknown</span>}
                  </span>
                  <span className="sms-conversation-time">{formatTime(c.lastAt)}</span>
                </div>
                <div className="sms-conversation-preview">
                  {c.lastDirection === 'Outbound' && (
                    c.lastAutomated
                      ? <span className="sms-preview-auto">Auto: </span>
                      : <span className="sms-preview-you">You: </span>
                  )}
                  {c.lastMessage}
                </div>
                {c.unread > 0 && <span className="sms-unread-dot">{c.unread}</span>}
              </li>
            ))}
          </ul>
        )}
      </aside>

      {/* ---------- Thread / composer ---------- */}
      <section className="sms-thread-pane">
        {composing ? (
          <div className="sms-compose-new">
            <div className="sms-compose-head">
              <button className="sms-back-btn" onClick={backToList} aria-label="Back to conversations">
                <i className="fas fa-chevron-left"></i>
              </button>
              <h3>New message</h3>
            </div>
            <input
              type="text"
              className="sms-search-input"
              placeholder="Search a contact by name…"
              value={search}
              onChange={(e) => runSearch(e.target.value)}
              autoFocus
            />
            <ul className="sms-search-results">
              {searchResults.map((cust) => (
                <li key={cust.id} onClick={() => startConversationWith(cust)}>
                  <span className="sms-search-name">
                    {cust.isCrew
                      ? cust.displayName
                      : `${cust.firstName || ''} ${cust.lastName || ''}`.trim() || cust.companyName || 'Unknown'}
                    {/* Says why someone with no contact record is in this list. */}
                    {cust.isCrew && <span className="sms-search-badge"> · crew</span>}
                  </span>
                  <span className="sms-search-phone">
                    {cust.isCrew
                      ? formatPhone(cust.phone)
                      : formatPhone(custPhone(cust)) || 'no phone on file'}
                  </span>
                </li>
              ))}
              {search.trim().length >= 2 && searchResults.length === 0 && (
                <li className="sms-search-none">No contacts or crew found.</li>
              )}
            </ul>
          </div>
        ) : !selected ? (
          <div className="sms-thread-empty">
            <i className="fas fa-comments"></i>
            <p>Select a conversation, or start a new one.</p>
          </div>
        ) : (
          <>
            <div className="sms-thread-header">
              <button className="sms-back-btn" onClick={backToList} aria-label="Back to conversations">
                <i className="fas fa-chevron-left"></i>
              </button>
              <span className="sms-thread-name">{selected.name}</span>
            </div>

            <div className="sms-thread-messages" ref={threadBoxRef}>
              {loadingThread ? (
                <div className="sms-thread-loading">Loading…</div>
              ) : thread.length === 0 ? (
                <div className="sms-thread-start">No messages yet — say hello 👋</div>
              ) : (
                thread.map((m) => (
                  <div
                    key={m.id}
                    className={`sms-bubble-row ${m.direction === 'Outbound' ? 'out' : 'in'}`}
                  >
                    <div className={`sms-bubble${m.status === 'failed' ? ' failed' : ''}${m.automated ? ' automated' : ''}`}>
                      {m.automated && (
                        <div className="sms-bubble-auto-tag">
                          <i className="fas fa-bolt"></i> Automated
                        </div>
                      )}
                      {m.media && m.media.length > 0 && (
                        <div className="sms-bubble-media">
                          {m.media.map((md, i) => (
                            <div key={i} className="sms-media-item">
                              <img
                                src={mediaSrc(md.url)}
                                alt="attachment"
                                className="sms-media-thumb"
                                onClick={() => setLightbox(mediaSrc(md.url))}
                              />
                              {/* A real link, not a fetch-and-blob: the server sends
                                  Content-Disposition, so the browser saves it directly
                                  and nothing is held in memory. */}
                              <a
                                className="sms-media-download"
                                href={downloadSrc(md.url)}
                                onClick={(e) => e.stopPropagation()}
                                title="Download"
                              >
                                <i className="fas fa-download"></i>
                              </a>
                            </div>
                          ))}
                          {m.media.length > 1 && (
                            <button
                              type="button"
                              className="sms-media-download-all"
                              onClick={() => downloadAll(m.media)}
                            >
                              <i className="fas fa-download"></i> Save all {m.media.length}
                            </button>
                          )}
                        </div>
                      )}
                      {m.body && <div className="sms-bubble-body">{m.body}</div>}
                      <div className="sms-bubble-meta">
                        {formatTime(m.createdAt)}
                        {m.direction === 'Outbound' && (
                          <span className="sms-bubble-status"> · {m.status}</span>
                        )}
                        {/* Without this a failed send just says "failed" and
                            there's nothing to act on. */}
                        {m.errorMessage && (
                          <span className="sms-bubble-error"> — {m.errorMessage}</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}

            </div>

            {error && <div className="sms-error">{error}</div>}

            {attachments.length > 0 && (
              <div className="sms-attach-tray">
                {attachments.map((a) => (
                  <div className="sms-attach-chip" key={a.key}>
                    <img src={a.previewUrl} alt="attachment" />
                    <button className="sms-attach-remove" onClick={() => removeAttachment(a.key)} title="Remove">×</button>
                  </div>
                ))}
                {uploading && <div className="sms-attach-uploading"><i className="fas fa-spinner fa-spin"></i></div>}
              </div>
            )}

            <div className="sms-composer">
              <input
                type="file"
                ref={fileInputRef}
                accept="image/*"
                multiple
                style={{ display: 'none' }}
                onChange={handlePickFiles}
              />
              <button
                className="sms-attach-btn"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                title="Attach photo"
              >
                <i className="fas fa-image"></i>
              </button>
              <button
                className="sms-attach-btn"
                onClick={() => setShowCamera(true)}
                disabled={uploading}
                title="Take photo"
              >
                <i className="fas fa-camera"></i>
              </button>
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={handleComposerKeyDown}
                /* Just "Type a message". The keyboard hint was instructions for a
                   keyboard half the readers do not have, and on a phone it filled
                   the box so the actual prompt was cut off. Enter still sends. */
                placeholder="Type a message…"
                rows={2}
              />
              <button
                className="sms-send-btn"
                onClick={handleSend}
                disabled={sending || uploading || (!draft.trim() && attachments.length === 0)}
              >
                {sending ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-paper-plane"></i>}
              </button>
            </div>
          </>
        )}
      </section>

      {showCamera && (
        <CameraCapture onCapture={handleCameraCapture} onClose={() => setShowCamera(false)} />
      )}

      {lightbox && (
        <div className="sms-lightbox" onClick={() => setLightbox(null)}>
          <img src={lightbox} alt="attachment" onClick={(e) => e.stopPropagation()} />
          {/* The lightbox is where you decide whether a photo is worth keeping, so
              the save lives here too rather than only on the thumbnail. */}
          <a
            className="sms-lightbox-download"
            href={downloadSrc(lightbox)}
            onClick={(e) => e.stopPropagation()}
          >
            <i className="fas fa-download"></i> Save
          </a>
          <button className="sms-lightbox-close" onClick={() => setLightbox(null)}>×</button>
        </div>
      )}
    </div>
  );
};

export default SmsInbox;
